import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const AgreementDocumentsModal = ({
  isOpen,
  onClose,
  onSuccess,
  gym
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState('upload'); // upload, uploading, success, error
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([
    { id: 1, name: '', file: null, fileName: '' }
  ]);
  const fileInputRefs = useRef({});

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && gym) {
      setStep('upload');
      setDocuments([{ id: 1, name: '', file: null, fileName: '' }]);
      setError(null);
    }
  }, [isOpen, gym]);

  const addDocument = () => {
    const newId = Math.max(...documents.map(d => d.id)) + 1;
    setDocuments([...documents, { id: newId, name: '', file: null, fileName: '' }]);
  };

  const removeDocument = (id) => {
    if (documents.length === 1) return; // Keep at least one document row
    setDocuments(documents.filter(d => d.id !== id));
  };

  const updateDocumentName = (id, name) => {
    setDocuments(documents.map(d =>
      d.id === id ? { ...d, name } : d
    ));
  };

  const handleFileSelect = (id, file) => {
    if (file) {
      setDocuments(documents.map(d =>
        d.id === id ? { ...d, file, fileName: file.name } : d
      ));
    }
  };

  const triggerFileInput = (id) => {
    if (fileInputRefs.current[id]) {
      fileInputRefs.current[id].click();
    }
  };

  const removeFile = (id) => {
    setDocuments(documents.map(d =>
      d.id === id ? { ...d, file: null, fileName: '' } : d
    ));
    // Clear the file input
    if (fileInputRefs.current[id]) {
      fileInputRefs.current[id].value = '';
    }
  };

  const validateDocuments = () => {
    // Check if at least one complete document entry exists
    const completeDocuments = documents.filter(d => d.name.trim() && d.file);

    if (completeDocuments.length === 0) {
      setError('Please add at least one document with a name and file');
      return false;
    }

    // Check for documents with name but no file or vice versa
    const incompleteDocuments = documents.filter(d =>
      (d.name.trim() && !d.file) || (!d.name.trim() && d.file)
    );

    if (incompleteDocuments.length > 0) {
      setError('Please complete all document entries (both name and file required)');
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!validateDocuments()) return;

    try {
      setStep('uploading');
      setError(null);

      const formData = new FormData();
      formData.append('gymId', gym.id);

      // Add only complete documents
      const completeDocuments = documents.filter(d => d.name.trim() && d.file);

      completeDocuments.forEach((doc, index) => {
        formData.append('documents', doc.file);
        formData.append(`documentNames[${index}]`, doc.name.trim());
      });

      const response = await axios.post('/api/gyms/agreement-documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setStep('success');

      // Notify parent after short delay
      setTimeout(() => {
        onSuccess({ gym: response.data.gym, documentsUploaded: response.data.documentsUploaded });
      }, 1500);

    } catch (error) {
      console.error('Error uploading documents:', error);
      setError(error.response?.data?.error || 'Failed to upload documents');
      setStep('error');
    }
  };

  const handleClose = () => {
    if (step === 'uploading') return; // Don't close while uploading
    onClose();
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext)) {
      return (
        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    if (['doc', 'docx'].includes(ext)) {
      return (
        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return (
        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      return (
        <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-green-600 to-green-700">
          <div className="text-white">
            <h2 className="text-xl font-semibold">Agreement Documents</h2>
            <p className="text-sm text-green-100 mt-1">
              {gym?.name} - Upload required documents
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-green-200"
            disabled={step === 'uploading'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-green-800">Upload Agreement Documents</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Add documents related to the agreement. Give each document a descriptive name and upload the file.
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Document List */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium text-gray-700">Documents</h4>
                  <span className="text-xs text-gray-500">
                    {documents.filter(d => d.name.trim() && d.file).length} complete
                  </span>
                </div>

                {documents.map((doc, index) => (
                  <div
                    key={doc.id}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div className="flex items-start gap-4">
                      {/* Document Number */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-green-700">{index + 1}</span>
                      </div>

                      {/* Document Details */}
                      <div className="flex-1 space-y-3">
                        {/* Document Name Input */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Document Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={doc.name}
                            onChange={(e) => updateDocumentName(doc.id, e.target.value)}
                            placeholder="e.g., Signed Agreement, ID Proof, Property Papers..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        {/* File Upload */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            File <span className="text-red-500">*</span>
                          </label>

                          <input
                            type="file"
                            ref={(el) => fileInputRefs.current[doc.id] = el}
                            onChange={(e) => handleFileSelect(doc.id, e.target.files[0])}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp"
                            className="hidden"
                          />

                          {doc.file ? (
                            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2">
                              <div className="flex items-center">
                                {getFileIcon(doc.fileName)}
                                <span className="ml-2 text-sm text-gray-700 truncate max-w-[200px]">
                                  {doc.fileName}
                                </span>
                                <span className="ml-2 text-xs text-gray-400">
                                  ({(doc.file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(doc.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => triggerFileInput(doc.id)}
                              className="w-full border-2 border-dashed border-gray-300 rounded-md px-3 py-4 text-center hover:border-green-400 hover:bg-green-50 transition-colors"
                            >
                              <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <span className="text-sm text-gray-500">Click to upload file</span>
                              <span className="block text-xs text-gray-400 mt-1">
                                PDF, Word, Excel, CSV, or Images (max 10MB)
                              </span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Remove Button */}
                      {documents.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDocument(doc.id)}
                          className="flex-shrink-0 text-gray-400 hover:text-red-500 p-1"
                          title="Remove document"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add More Button */}
                <button
                  type="button"
                  onClick={addDocument}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-green-300 rounded-lg text-green-600 hover:bg-green-50 hover:border-green-400 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="font-medium">Add Another Document</span>
                </button>
              </div>

              {/* Common Document Types Suggestion */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="text-xs font-medium text-gray-600 mb-2">Common Document Types:</h5>
                <div className="flex flex-wrap gap-2">
                  {['Signed Agreement', 'ID Proof', 'Address Proof', 'Property Papers', 'Bank Details', 'GST Certificate', 'PAN Card', 'Photo'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        const emptyDoc = documents.find(d => !d.name.trim());
                        if (emptyDoc) {
                          updateDocumentName(emptyDoc.id, type);
                        } else {
                          const newId = Math.max(...documents.map(d => d.id)) + 1;
                          setDocuments([...documents, { id: newId, name: type, file: null, fileName: '' }]);
                        }
                      }}
                      className="px-2 py-1 text-xs bg-white border border-gray-300 rounded-md hover:bg-green-50 hover:border-green-300 transition-colors"
                    >
                      + {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Uploading Step */}
          {step === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 border-t-green-600 mb-6"></div>
              <h3 className="text-lg font-semibold text-gray-800">Uploading Documents...</h3>
              <p className="text-sm text-gray-500 mt-2">
                Please wait while we upload {documents.filter(d => d.name.trim() && d.file).length} document(s)
              </p>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Documents Uploaded!</h3>
              <p className="text-sm text-gray-500 mt-2 text-center">
                {documents.filter(d => d.name.trim() && d.file).length} document(s) uploaded successfully for<br />
                <strong className="text-gray-700">{gym?.name}</strong>
              </p>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Upload Failed</h3>
              <p className="text-sm text-red-600 mt-2 text-center">{error}</p>

              <button
                onClick={() => setStep('upload')}
                className="mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {step === 'upload' && (
              <span>
                {documents.filter(d => d.name.trim() && d.file).length} of {documents.length} documents ready
              </span>
            )}
          </div>

          <div className="flex space-x-3">
            {step === 'upload' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={documents.filter(d => d.name.trim() && d.file).length === 0}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload Documents
                </button>
              </>
            )}

            {step === 'success' && (
              <button
                onClick={handleClose}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                Done
              </button>
            )}

            {step === 'error' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementDocumentsModal;
